import React from 'react';

interface OptimizedImageProps {
  src: string; // PNG source path
  srcWebp?: string; // WebP source path (optional, will try to auto-generate if not provided)
  srcAvif?: string; // AVIF source path (optional, will try to auto-generate if not provided)
  alt: string;
  className?: string;
  loading?: 'eager' | 'lazy';
  sizes?: string;
  width?: number;
  height?: number;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * OptimizedImage component that automatically serves next-gen image formats
 * (AVIF, WebP) with fallback to the original PNG format.
 * 
 * This component uses the HTML <picture> element to let the browser
 * choose the best supported format automatically.
 * 
 * You can either:
 * 1. Provide explicit paths for all formats (recommended for production builds)
 * 2. Let the component auto-generate paths by replacing extensions (for development)
 */
export default function OptimizedImage({
  src,
  srcWebp,
  srcAvif,
  alt,
  className = '',
  loading = 'lazy',
  sizes,
  width,
  height,
  onLoad,
  onError
}: OptimizedImageProps) {
  // Generate paths for different formats if not explicitly provided
  const getOptimizedSrc = (format: 'avif' | 'webp') => {
    return src.replace(/\.png$/i, `.${format}`);
  };

  // Use provided paths or generate them
  const avifSrc = srcAvif || getOptimizedSrc('avif');
  const webpSrc = srcWebp || getOptimizedSrc('webp');

  return (
    <picture>
      {/* AVIF format - best compression, modern browsers */}
      <source 
        srcSet={avifSrc} 
        type="image/avif"
        sizes={sizes}
      />
      
      {/* WebP format - good compression, wide support */}
      <source 
        srcSet={webpSrc} 
        type="image/webp"
        sizes={sizes}
      />
      
      {/* Fallback PNG for older browsers */}
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        width={width}
        height={height}
        onLoad={onLoad}
        onError={onError}
        // Add decode="async" for better performance
        decoding="async"
      />
    </picture>
  );
}

// Type-safe helper for image imports
export interface ImageAsset {
  png: string;
  webp: string;
  avif: string;
}

// Helper function to create image asset objects
export function createImageAsset(pngPath: string): ImageAsset {
  return {
    png: pngPath,
    webp: pngPath.replace('.png', '.webp'),
    avif: pngPath.replace('.png', '.avif')
  };
} 